import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import { useAuth } from '../../context/AuthContext';

const functions = getFunctions();
const operatorChat = httpsCallable(functions, 'operatorChat');
const generateSessionPdf = httpsCallable(functions, 'generateSessionPdf');

const LESSON_LABELS = ['Assessment', 'Foundation', 'Application', 'Synthesis'];

export default function OperatorChat() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [lessonCount, setLessonCount] = useState(0);
  const [assessedLevel, setAssessedLevel] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [demoSessionId, setDemoSessionId] = useState('');
  const [showReset, setShowReset] = useState(false);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);

  // Load or create session
  useEffect(() => {
    if (!currentUser) return;
    async function loadSession() {
      const sid = `${currentUser.uid}-block-1`;
      setSessionId(sid);
      const demoSid = localStorage.getItem('demo_session_id') || '';
      setDemoSessionId(demoSid);

      let snap;
      try {
        snap = await getDoc(doc(db, 'chat_sessions', sid));
      } catch {
        // Firestore read failed — proceed as a new session
        snap = { exists: () => false };
      }
      if (snap.exists()) {
        const data = snap.data();
        setLessonCount(data.lessonCount || 0);
        setAssessedLevel(data.assessedLevel || null);
        setLimitReached((data.lessonCount || 0) >= 4);
        if (data.history && data.history.length > 0) {
          // Returning session — restore history
          setMessages(data.history.map(m => ({ role: m.role, content: m.content })));
          setSessionLoaded(true);
        } else {
          // New session — get personalized opening from AI
          setSessionLoaded(true);
          setLoading(true);
          try {
            const result = await operatorChat({ sessionId: sid, messages: [], demoSessionId: demoSid });
            setMessages([{ role: 'assistant', content: result.data.reply }]);
            if (demoSid) setShowReset(true);
          } catch {
            setMessages([{ role: 'assistant', content: "Let's get to work." }]);
          } finally {
            setLoading(false);
          }
        }
      } else {
        // Brand new user — get personalized opening
        setSessionLoaded(true);
        setLoading(true);
        try {
          const result = await operatorChat({ sessionId: sid, messages: [], demoSessionId: demoSid });
          setMessages([{ role: 'assistant', content: result.data.reply }]);
          if (demoSid) setShowReset(true);
        } catch {
          setMessages([{ role: 'assistant', content: "Let's get to work." }]);
        } finally {
          setLoading(false);
        }
      }
    }
    loadSession();
  }, [currentUser]);

  // Auto-scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Auto-focus textarea after bot finishes — runs after scroll so scroll doesn't steal focus
  useEffect(() => {
    if (!loading && !limitReached) {
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [loading, limitReached]);

  async function sendMessage() {
    if (!input.trim() || loading || limitReached) return;
    const userMsg = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const result = await operatorChat({
        sessionId,
        messages: newMessages,
        demoSessionId: demoSessionId || '',
      });

      const { reply, lessonCount: newCount, assessedLevel: newLevel, limitReached: limited, banned } = result.data;
      if (banned) { navigate('/purgatory'); return; }
      const updatedMessages = [...newMessages, { role: 'assistant', content: reply }];
      setMessages(updatedMessages);
      setLessonCount(newCount);
      setAssessedLevel(newLevel);
      setLimitReached(limited);

      // Autosave to Firestore after every AI response
      await setDoc(doc(db, 'chat_sessions', sessionId), {
        uid: currentUser.uid,
        lessonCount: newCount,
        assessedLevel: newLevel,
        lastMessage: serverTimestamp(),
        history: updatedMessages.slice(-20).map(m => ({ role: m.role, content: m.content })),
      }, { merge: true });

    } catch (err) {
      if (err.code === 'functions/resource-exhausted') {
        setLimitReached(true);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'Something went wrong. Try again in a moment.'
        }]);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleReset() {
    setShowReset(false);
    localStorage.removeItem('demo_session_id');
    setDemoSessionId('');
    setMessages([]);
    setLoading(true);
    try {
      const result = await operatorChat({ sessionId, messages: [], demoSessionId: '' });
      setMessages([{ role: 'assistant', content: result.data.reply }]);
    } catch {
      setMessages([{ role: 'assistant', content: "Let's get to work." }]);
    } finally {
      setLoading(false);
    }
  }

  async function handlePause(label = 'pause') {
    if (messages.length < 3) {
      // Not enough to generate a meaningful report
      navigate('/dashboard');
      return;
    }
    setPdfGenerating(true);
    setPdfError(null);
    try {
      await generateSessionPdf({ sessionId });
      navigate(`/session-report?session=${sessionId}&action=${label}`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setPdfError('Could not generate your report. Redirecting…');
      setTimeout(() => navigate(`/session-report?session=${sessionId}&action=${label}`), 1500);
    } finally {
      setPdfGenerating(false);
    }
  }

  if (!sessionLoaded) {
    return (
      <div style={styles.page}>
        <div style={{ color: '#4f8ef7', fontSize: '14px' }}>Loading session...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>

      {/* PDF generating overlay */}
      {pdfGenerating && (
        <div style={styles.pdfOverlay}>
          <div style={styles.pdfOverlayInner}>
            <div style={styles.pdfSpinner} />
            <div style={{ color: '#f0f4ff', fontSize: '15px', marginTop: '16px' }}>Generating your session report…</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.logo}>THE OPERATOR METHOD</div>
          <div style={styles.lessonPips}>
            {LESSON_LABELS.map((label, i) => (
              <div key={label} style={{
                ...styles.pip,
                backgroundColor: i < lessonCount ? '#4f8ef7' : i === lessonCount ? '#1e3a5f' : '#0d1220',
                borderColor: i < lessonCount ? '#4f8ef7' : i === lessonCount ? '#4f8ef7' : '#1e2a3a',
              }}>
                <span style={{ fontSize: '10px', color: i < lessonCount ? '#fff' : i === lessonCount ? '#4f8ef7' : '#3a4560' }}>
                  {i < lessonCount ? '\u2713' : i + 1}
                </span>
              </div>
            ))}
            {assessedLevel && (
              <div style={styles.levelBadge}>{assessedLevel}</div>
            )}
          </div>
        </div>
        <div style={styles.headerRight}>
          {messages.length >= 3 && !pdfGenerating && (
            <button onClick={() => handlePause('pause')} style={styles.pauseBtn}
              title="Save your progress and generate a session report">
              Pause
            </button>
          )}
          {(limitReached || lessonCount >= 4) && !pdfGenerating && (
            <button onClick={() => handlePause('done')} style={styles.doneBtn}>
              Done →
            </button>
          )}
          <button onClick={() => navigate('/dashboard')} style={styles.backBtn}>← Dashboard</button>
        </div>
      </div>

      {/* Error banner */}
      {pdfError && (
        <div style={styles.errorBanner}>{pdfError}</div>
      )}

      {/* Chat window */}
      <div style={styles.chatWindow}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            ...styles.messageBubble,
            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
          }}>
            {msg.role === 'assistant' && (
              <div style={styles.aiLabel}>INSTRUCTOR</div>
            )}
            <div style={msg.role === 'user' ? styles.userBubble : styles.aiBubble}>
              {msg.content.split('\n').map((line, j) => (
                <span key={j}>{line}{j < msg.content.split('\n').length - 1 && <br />}</span>
              ))}
            </div>
          </div>
        ))}

        {showReset && !loading && (
          <div style={styles.resetBanner}>
            <span style={styles.resetText}>This session was built from your evaluation. If that wasn't your real work — start clean.</span>
            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button onClick={() => setShowReset(false)} style={styles.resetDismiss}>Keep context</button>
              <button onClick={handleReset} style={styles.resetBtn}>Start fresh</button>
            </div>
          </div>
        )}

        {loading && (
          <div style={{ ...styles.messageBubble, alignSelf: 'flex-start' }}>
            <div style={styles.aiLabel}>INSTRUCTOR</div>
            <div style={styles.aiBubble}>
              <span style={styles.thinking}>thinking<span className="dots"><span>.</span><span>.</span><span>.</span></span></span>
            </div>
          </div>
        )}

        {limitReached && (
          <div style={styles.limitCard}>
            <div style={styles.limitTitle}>Block Complete</div>
            <p style={styles.limitBody}>
              You've completed your first 4-lesson block.
              {assessedLevel && ` Your assessed level: ${assessedLevel}.`}
            </p>
            <p style={styles.limitBody}>
              Ready to continue? The next block picks up exactly where your understanding left off.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => handlePause('done')} style={styles.limitBtnSecondary}>
                {pdfGenerating ? 'Generating report…' : 'Save Report \u2193'}
              </button>
              <button onClick={() => navigate('/pricing')} style={styles.limitBtn}>
                Continue to Next Block →
              </button>
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {!limitReached && (
        <div style={styles.inputRow}>
          <textarea
            id="operator-chat-input"
            name="operator-chat-input"
            ref={textareaRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Type your response... (Enter to send, Shift+Enter for new line)"
            style={styles.textarea}
            rows={2}
            disabled={loading}
          />
          <button onClick={sendMessage} disabled={loading || !input.trim()} style={styles.sendBtn}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0e1a',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#f0f4ff',
  },
  pdfOverlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(10,14,26,0.90)',
    zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  pdfOverlayInner: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  pdfSpinner: {
    width: '36px', height: '36px',
    border: '3px solid #1e2a3a',
    borderTop: '3px solid #4f8ef7',
    borderRadius: '50%',
  },
  errorBanner: {
    backgroundColor: '#1a0d0d', borderBottom: '1px solid #5a1a1a',
    color: '#ff6b6b', padding: '10px 24px', fontSize: '13px', textAlign: 'center',
  },
  header: {
    padding: '16px 24px',
    borderBottom: '1px solid #1e2a3a',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#0a0e1a',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  logo: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: '#4f8ef7',
  },
  lessonPips: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pip: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 600,
  },
  levelBadge: {
    marginLeft: '8px',
    padding: '3px 10px',
    backgroundColor: '#0f2040',
    border: '1px solid #4f8ef7',
    borderRadius: '12px',
    fontSize: '11px',
    color: '#4f8ef7',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4a5568',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '6px 12px',
  },
  pauseBtn: {
    padding: '6px 14px',
    backgroundColor: 'transparent',
    border: '1px solid #2a3a50',
    color: '#8a9bbf',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  doneBtn: {
    padding: '6px 16px',
    backgroundColor: '#0f2040',
    border: '1px solid #4f8ef7',
    color: '#4f8ef7',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  chatWindow: {
    flex: 1,
    padding: '24px',
    maxWidth: '760px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    overflowY: 'auto',
    paddingBottom: '120px',
  },
  messageBubble: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '80%',
  },
  aiLabel: {
    fontSize: '10px',
    letterSpacing: '0.15em',
    color: '#4f8ef7',
    marginBottom: '6px',
    fontWeight: 600,
  },
  aiBubble: {
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '2px 16px 16px 16px',
    padding: '14px 18px',
    fontSize: '15px',
    lineHeight: 1.7,
    color: '#c8d0e0',
  },
  userBubble: {
    backgroundColor: '#0f2040',
    border: '1px solid #1e3a5f',
    borderRadius: '16px 16px 2px 16px',
    padding: '14px 18px',
    fontSize: '15px',
    lineHeight: 1.7,
    color: '#f0f4ff',
  },
  thinking: {
    color: '#4a5568',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  inputRow: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px 24px',
    backgroundColor: '#0a0e1a',
    borderTop: '1px solid #1e2a3a',
    display: 'flex',
    gap: '12px',
    maxWidth: '760px',
    margin: '0 auto',
    width: '100%',
  },
  textarea: {
    flex: 1,
    backgroundColor: '#0d1220',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    color: '#f0f4ff',
    fontSize: '15px',
    padding: '12px 16px',
    resize: 'none',
    fontFamily: 'inherit',
    outline: 'none',
    lineHeight: 1.5,
  },
  sendBtn: {
    padding: '12px 24px',
    backgroundColor: '#4f8ef7',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-end',
  },
  limitCard: {
    backgroundColor: '#0d1220',
    border: '1px solid #4f8ef7',
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    maxWidth: '480px',
    margin: '20px auto',
  },
  limitTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#f0f4ff',
    marginBottom: '12px',
  },
  limitBody: {
    fontSize: '15px',
    color: '#6b7a99',
    lineHeight: 1.7,
    margin: '0 0 16px 0',
  },
  limitBtn: {
    padding: '12px 32px',
    backgroundColor: '#4f8ef7',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '8px',
  },
  limitBtnSecondary: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#8a9bbf',
    border: '1px solid #2a3a50',
    borderRadius: '10px',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '8px',
  },
  resetBanner: {
    alignSelf: 'flex-start',
    backgroundColor: '#0d1626',
    border: '1px solid #2a3a50',
    borderRadius: '12px',
    padding: '14px 18px',
    maxWidth: '420px',
    marginBottom: '4px',
  },
  resetText: {
    fontSize: '13px',
    color: '#8a9bbf',
    lineHeight: 1.6,
  },
  resetDismiss: {
    padding: '7px 16px',
    backgroundColor: 'transparent',
    color: '#8a9bbf',
    border: '1px solid #2a3a50',
    borderRadius: '8px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  resetBtn: {
    padding: '7px 16px',
    backgroundColor: 'transparent',
    color: '#4f8ef7',
    border: '1px solid #4f8ef7',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
